import { useEffect, useRef, useState, type ReactNode } from 'react';

export interface MenuItem {
  label: string;
  hint?: string;
  onSelect: () => void;
  disabled?: boolean;
  danger?: boolean;
}

/** Menu suspenso simples (≡ da lista, ⋯ da linha). Fecha ao clicar fora ou com Esc. */
export function Menu({
  trigger,
  groups,
  align = 'right',
}: {
  trigger: (toggle: () => void) => ReactNode;
  groups: Array<{ title?: string; items: MenuItem[] }>;
  align?: 'left' | 'right';
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      {trigger(() => setOpen((current) => !current))}
      {open && (
        <div
          role="menu"
          className={`absolute z-30 mt-1 w-64 rounded-xl border border-slate-200 bg-white py-1 shadow-xl ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          {groups.map((group, index) => (
            <div
              key={group.title ?? index}
              className={index > 0 ? 'border-t border-slate-100' : ''}
            >
              {group.title && (
                <p className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  {group.title}
                </p>
              )}
              {group.items.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  role="menuitem"
                  disabled={item.disabled}
                  onClick={() => {
                    setOpen(false);
                    item.onSelect();
                  }}
                  className={`block w-full px-3 py-2 text-left text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 ${
                    item.danger ? 'text-red-600' : 'text-slate-700'
                  }`}
                >
                  {item.label}
                  {item.hint && (
                    <span className="block text-xs text-slate-400">{item.hint}</span>
                  )}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
